import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, TrendingUp, Users, Euro, FileText, Lightbulb } from 'lucide-react';

const KeyInsights = () => {
  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8">
      {/* Header */}
      <div className="bg-[#0F203C] text-white py-8 px-6 mb-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-2 tracking-tight">Insights Clés - Analyse IFOP 2024</h1>
          <p className="text-lg opacity-90">Synthèse des résultats de l'enquête sociologique</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Main Finding */}
        <Card className="border-[#E2E8F0] shadow-lg mb-8 bg-gradient-to-r from-[#0F203C] to-[#1a3a5c] text-white">
          <CardHeader>
            <CardTitle className="text-3xl font-bold flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-[#D4AF37]" />
              Constat Principal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl leading-relaxed">
              <span className="font-bold text-[#D4AF37]">72%</span> des répondants considèrent leur bien concerné par le risque d'inondation, 
              mais seulement <span className="font-bold text-[#D4AF37]">39%</span> ont équipé leur bien de batardeaux.
            </p>
            <p className="mt-4 text-lg opacity-90">
              → Un écart significatif entre la conscience du risque et l'action préventive.
            </p>
          </CardContent>
        </Card>

        {/* Key Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
                Recommandation Diagnostic
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-[#16A34A] mb-2">78%</div>
              <p className="text-sm text-[#475569]">
                Des diagnostics préconisent l'installation de batardeaux
              </p>
              <div className="mt-4 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Recommandation</span>
                  <span className="font-semibold">74%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Obligation</span>
                  <span className="font-semibold">22%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <Users className="w-4 h-4 text-[#D4AF37]" />
                Satisfaction Utilisateurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-[#D4AF37] mb-2">86%</div>
              <p className="text-sm text-[#475569] mb-4">
                Des utilisateurs sont satisfaits de leurs batardeaux
              </p>
              <div className="bg-[#D4AF37]/10 p-3 rounded">
                <p className="text-sm font-semibold text-[#0F203C]">
                  97% recommanderaient l'installation à leur entourage
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#0F203C]" />
                Efficacité Diagnostic
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-[#0F203C] mb-2">75%</div>
              <p className="text-sm text-[#475569]">
                Des équipés ont suivi les conseils de leur diagnostic
              </p>
              <p className="mt-4 text-xs text-[#475569] italic">
                Le diagnostic est le principal catalyseur d'action
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Barriers Section */}
        <Card className="border-[#E2E8F0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#0F203C] flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-[#DC2626]" />
              Freins à l'Adoption (61% non équipés)
            </CardTitle>
            <CardDescription>Principales raisons de non-installation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                  <Euro className="w-8 h-8 text-[#DC2626]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-[#0F203C]">Moyens financiers insuffisants</h4>
                    <span className="text-2xl font-bold text-[#DC2626]">26%</span>
                  </div>
                  <p className="text-sm text-[#475569]">Coût initial et nécessité d'avancer les frais</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-8 h-8 text-[#DC2626]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-[#0F203C]">Risque perçu comme faible</h4>
                    <span className="text-2xl font-bold text-[#DC2626]">23%</span>
                  </div>
                  <p className="text-sm text-[#475569]">Sous-estimation du risque malgré le diagnostic</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-8 h-8 text-[#DC2626]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-[#0F203C]">Difficulté à trouver un installateur</h4>
                    <span className="text-2xl font-bold text-[#DC2626]">23%</span>
                  </div>
                  <p className="text-sm text-[#475569]">Manque d'information sur les professionnels</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-[#DC2626]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-[#0F203C]">Démarches administratives complexes</h4>
                    <span className="text-2xl font-bold text-[#DC2626]">18%</span>
                  </div>
                  <p className="text-sm text-[#475569]">Procédures de subventions perçues comme lourdes</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Experience Section */}
        <Card className="border-[#E2E8F0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#0F203C] flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-[#16A34A]" />
              Expérience des Utilisateurs Équipés
            </CardTitle>
            <CardDescription>Retour d'expérience positif</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#16A34A]/5 p-4 rounded-md border border-[#16A34A]/20">
                <div className="text-3xl font-bold text-[#16A34A] mb-2">84%</div>
                <p className="text-sm font-semibold text-[#0F203C] mb-1">Facilité de contact</p>
                <p className="text-xs text-[#475569]">Des installateurs facilement joignables</p>
              </div>
              <div className="bg-[#16A34A]/5 p-4 rounded-md border border-[#16A34A]/20">
                <div className="text-3xl font-bold text-[#16A34A] mb-2">96%</div>
                <p className="text-sm font-semibold text-[#0F203C] mb-1">Autonomie d'installation</p>
                <p className="text-xs text-[#475569]">Capables de manipuler seuls</p>
              </div>
              <div className="bg-[#16A34A]/5 p-4 rounded-md border border-[#16A34A]/20">
                <div className="text-3xl font-bold text-[#16A34A] mb-2">98%</div>
                <p className="text-sm font-semibold text-[#0F203C] mb-1">Accessibilité</p>
                <p className="text-xs text-[#475569]">Accès facile en cas d'alerte</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="border-[#D4AF37] shadow-lg bg-[#D4AF37]/5">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#0F203C] flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-[#D4AF37]" />
              Recommandations Stratégiques
            </CardTitle>
            <CardDescription>Leviers d'action pour améliorer l'adoption</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-[#0F203C] mb-1">Faciliter l'accès à l'information</h4>
                  <p className="text-sm text-[#475569]">
                    Fournir des listes d'installateurs agréés directement dans les diagnostics. 
                    43% ignorent l'existence des subventions locales.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-[#0F203C] mb-1">Simplifier les démarches de subvention</h4>
                  <p className="text-sm text-[#475569]">
                    Mettre en place des mécanismes de paiement direct aux installateurs pour éviter l'avance de frais, 
                    principale barrière financière.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-[#0F203C] mb-1">Sensibiliser à la réalité du risque</h4>
                  <p className="text-sm text-[#475569]">
                    Développer des campagnes utilisant des témoignages d'utilisateurs satisfaits (97% recommanderaient) 
                    et des données locales concrètes.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-[#0F203C] mb-1">Renforcer le rôle du diagnostic</h4>
                  <p className="text-sm text-[#475569]">
                    Le diagnostic est le principal déclencheur (75%). Garantir sa qualité et son suivi personnalisé 
                    est essentiel pour transformer la conscience en action.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-[#475569] pb-8">
          <p>Source : Enquête IFOP pour AFPCNT, Mission Risques Naturels et Mayane - Janvier 2024</p>
          <p className="mt-1">Base : 318 répondants ayant réalisé un diagnostic de vulnérabilité d'inondation</p>
        </div>
      </div>
    </div>
  );
};

export default KeyInsights;
